import { cleanSpaces } from "../../../../utils/utils";
import {
  createEventService,
  getContractService,
  getEventService,
  getEventTypesService,
  updateNewShieldEventFile,
} from "../../../../../services/commonService";
import { getUserService } from "../../../../../services/userServices";
import { getDeploymentService } from "../../../../../services/deploymentServices";
import scoreboardTemplateStorage from "../../utils/scoreboard-template-storage";

const { storeScoreboardTemplate } = scoreboardTemplateStorage;

const LOCALE_MAP = {
  es: "es_ES",
  en: "en_US",
  gl: "gl_ES",
  ja: "ja_JP",
};

const promisifyGetEventType = eventTypeId =>
  new Promise(resolve => {
    getEventTypesService(eventTypeId, resolve);
  });

const promisifyGetContract = contractId =>
  new Promise(resolve => {
    getContractService(contractId, resolve);
  });

const promisifyGetDeployment = deploymentId =>
  new Promise((resolve, reject) => {
    getDeploymentService({ deploymentId }, resolve, reject);
  });

const getMsisdnQod = async contractId => {
  const contractData = await promisifyGetContract(contractId);
  if (!contractData?.deployment_id) return undefined;
  const deploymentData = await promisifyGetDeployment(contractData.deployment_id);
  return deploymentData?.msisdn_qod;
};

const promisifyGetUser = username =>
  new Promise((resolve, reject) => {
    getUserService(username, response => resolve(response.data), reject);
  });

const promisifyUploadLogo = (customerId, asset) =>
  new Promise((resolve, reject) => {
    updateNewShieldEventFile({ body: { file: asset }, customerId }, resolve, reject);
  });

const promisifyCreateEvent = body =>
  new Promise((resolve, reject) => {
    createEventService(body, resolve, reject);
  });

const promisifyGetEvent = eventId =>
  new Promise((resolve, reject) => {
    getEventService(eventId, resolve, reject);
  });

const mapLiveStreams = liveStreams =>
  liveStreams?.map(stream => ({
    type: stream.type,
    description: stream.description,
    rtmp_url: stream.rtmpUrl,
    key: stream.key,
    publishing_url: stream.publishingUrl,
    component_name: stream.componentName,
    component_status: stream.componentStatus,
  })) ?? [];

const mapSportEventData = sportEventData => ({
  team_name_1: sportEventData.teamName1 || "",
  team_name_1_abreviated: sportEventData.teamName1Abreviated || "",
  team_logo_1: sportEventData.teamLogo1 || "",
  team_name_2: sportEventData.teamName2 || "",
  team_name_2_abreviated: sportEventData.teamName2Abreviated || "",
  team_logo_2: sportEventData.teamLogo2 || "",
  alt_scoreboard_template_id: sportEventData.scoreboardTemplateId || "",
  scoreboard_type: sportEventData.scoreboardType || "",
});

const mapMatchPointConfig = matchPointConfig => ({
  ocurrences_source: matchPointConfig.ocurrencesSource,
  ocurrences_source_id: matchPointConfig.ocurrencesSourceId,
  ocurrences_visible: matchPointConfig.ocurrencesVisible,
});

const mapSubdeployment = subdeployment => ({
  id: subdeployment.id || subdeployment._id || "",
  name: subdeployment.name || "",
  description: subdeployment.description || "",
  streamsource_ids: subdeployment.streamsourceIds || subdeployment.streamsource_ids || [],
});

export default ({ history, hasAdminPrivileges, currentCustomerId, user }) => async values => {
  const { customerId, localRecInfo } = values;
  const cId = hasAdminPrivileges ? customerId : currentCustomerId;

  const [eventTypeData, userData] = await Promise.all([
    promisifyGetEventType(values.typeId),
    promisifyGetUser(user.sub),
  ]);

  const isSportEvent = eventTypeData.is_sport_event;
  const startedByUserId = userData._id;

  let sportEventDataPayload = null;

  if (isSportEvent && values.sportEventData) {
    const sportData = values.sportEventData;
    let teamLogo1 = sportData.teamLogo1 || "";
    let teamLogo2 = sportData.teamLogo2 || "";

    const hasNewLogo1 = !teamLogo1 && sportData.teamLogoAsset1?.image;
    const hasNewLogo2 = !teamLogo2 && sportData.teamLogoAsset2?.image;

    if (hasNewLogo1 || hasNewLogo2) {
      const contractData = await promisifyGetContract(values.contractId);
      const logoCustomerId = contractData.customer_id;

      const logoPromises = [];

      if (hasNewLogo1) {
        logoPromises.push(
          promisifyUploadLogo(logoCustomerId, sportData.teamLogoAsset1).then(result => {
            teamLogo1 = result._id;
          }),
        );
      }

      if (hasNewLogo2) {
        logoPromises.push(
          promisifyUploadLogo(logoCustomerId, sportData.teamLogoAsset2).then(result => {
            teamLogo2 = result._id;
          }),
        );
      }

      await Promise.all(logoPromises);
    }

    sportEventDataPayload = mapSportEventData({
      ...sportData,
      teamLogo1,
      teamLogo2,
    });
  }

  let localRecInfoPayload = null;
  const localRecEmails = !!localRecInfo?.local_rec_emails && cleanSpaces(localRecInfo.local_rec_emails).split(",");
  if (localRecEmails && localRecEmails.length) {
    localRecInfoPayload = {
      user_emails: localRecEmails.map(email => ({ email: email.trim(), locale: "es_ES" })),
    };
  }

  const subdeploymentPayload = values.subdeployment ? mapSubdeployment(values.subdeployment) : null;

  const matchPointConfigPayload = values.matchPointConfig?.ocurrencesSource
    ? mapMatchPointConfig(values.matchPointConfig)
    : null;

  const msisdnQod = values.activeQod ? await getMsisdnQod(values.contractId) : undefined;

  const body = {
    event_type_id: values.typeId,
    service_contract_id: values.contractId,
    event: {
      title: values.title,
      description: values.description,
      start_date: Math.floor(new Date(values.startDate).getTime() / 1000),
      end_date: Math.floor(new Date(values.endDate).getTime() / 1000),
    },
    started_by_user_id: startedByUserId,
    livestreams: mapLiveStreams(values.liveStreams),
    ...(sportEventDataPayload && { sport_event_data: sportEventDataPayload }),
    ...(matchPointConfigPayload && { match_point_config: matchPointConfigPayload }),
    ...(subdeploymentPayload && { subdeployment: subdeploymentPayload }),
    ...(localRecInfoPayload && { local_rec_info: localRecInfoPayload }),
    ...(values.graphicSetId && { graphic_set_id: values.graphicSetId }),
    active_qod: values.activeQod || false,
    ...(values.activeQod && msisdnQod && { msisdn_qod: msisdnQod }),
    is_muted_on_start: values.isMutedOnStart || false,
    unmute_minutes_before: values.isMutedOnStart ? Number(values.unmuteMinutesBefore ?? 0) : undefined,
    visible: values.visible,
    billable: values.billable,
    is_internal_test: values.isInternalTest || false,
    keywords: values.keywords || [],
    email_receipts: values.emailReceipts ? values.emailReceipts.map(entity => entity.email) : [],
    email_locale: values.emailReceipts
      ? values.emailReceipts.map(entity => LOCALE_MAP[entity.locale] ?? entity.locale ?? "es_ES")
      : [],
    room_template_id: values.roomTemplateId,
  };

  const createdEvent = await promisifyCreateEvent(body);

  const fullEvent = await promisifyGetEvent(createdEvent._id);

  if (fullEvent && fullEvent._id) {
    if (fullEvent.sport_event_data?.alt_scoreboard_template_id) {
      storeScoreboardTemplate(fullEvent.sport_event_data.alt_scoreboard_template_id);
    }
    history.replace(`/event/details/${fullEvent._id}/customer/${cId}`);
  }
};
